import React, { ChangeEvent, useEffect } from 'react';
import * as mobxReact from 'mobx-react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape, useIntl } from 'react-intl';
import './meeting-form.scss';
import classNames from 'classnames';
import { isSameDay } from 'date-fns';
import { CommonPopupBodyFooterLayout } from '../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../global/sub-window/common/common-close-sub-window-buttons';
import { FormField } from '../../../../global/form/form-field/form-field';
import { DefaultDatePicker } from '../../../../global/default-datepicker/default-datepicker';
import { Access, MeetingStatus, RecordingsStatus } from '../../../../../api/meetings.dao';
import OptionsButton from '../../../../global/options-button/options-button';
import { DateFnsFormats, IDateTimeFormatter, TimeFormats } from '../../../../../infrastructure/date-formatter';
import { getDisabled } from '../../../../../infrastructure/utils/attendee.utils';
import { TimeInput } from '../../../../global/time-input/time-input';
import DefaultButton from '../../../../global/default-button/default-button';
import { EnablementStatus, MAX_MEETING_DATES_LIST_LENGTH } from '../../../../../constants/common.constants';
import { IAttendeeDetailsDTO } from '../../../../../api/attendees.dao';
import { AsyncSearchInput } from '../../../../global/async-search-input/async-search-input';
import { CopyButton } from '../../../../global/copy-button/copy-button';
import { AddNewEntityButton } from '../../../../global/add-new-entity/add-new-entity-button';
import { Permission } from '../../../../../api/roles.dao';
import { hasPermission, isSuperUser } from '../../../../../infrastructure/utils/has-permissions';
import { RichTextEditor } from '../../../../global/rich-text-editor';
import { utcToZonedTime } from '../../../../../infrastructure/utils/date-fns-overrides.utils';
import { OEToggle } from '../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../global/text/text';
import { UploadImageS3Presentation } from '../../shared/upload-image/upload-image-to-s3.presentation';
import { DateTimeFormView } from './_date-time-form/date-time-form.presentation';
import { AttendeesList } from './_attendees-list/attendees-list';
import { RoomsDropdown } from './_rooms-dropdown/rooms-dropdown';
import MeetingFormVM from './meeting-form.viewmodel';
import { Links } from './_links/links.container';
import { InfoPopover } from '../../../../global/info-popover/info-popover';
import { ViewBlueprintDetailsSection } from '../../shared/view-blueprint-details-section/view-blueprint-details-section';
import * as COMMON_TEXTS from '../../../../../constants/common-labels.constants';
import * as MEETING_TEXTS from './meeting-form.texts';
import { V2_CONNECTIONS_TEXT } from '../../_rooms/_room-form/room-form.texts';
import { MeetingExpiryDate } from './_meeting-expiry-date/meeting-expiry-date';
import { MeetingGoLiveDate } from './_meeting-go-live-date/meeting-go-live-date';

interface IMeetingFormPresentationProps {
    vm: MeetingFormVM;
}

interface IRoles {
    name: string;
    key: string;
}

interface IIntlOptions {
    id: string;
    defaultMessage: string;
    description: string;
}

interface IOptions {
    optValue: string;
    label: IIntlOptions;
}
interface ISelectorProps {
    onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
    intl: IntlShape;
    options: IOptions[];
    value: string | number | null;
}

const roles: IRoles[] = [
    { name: 'Presenters', key: 'selectedPresenters', },
    { name: 'Participants', key: 'selectedParticipants' },
    { name: 'Hosts', key: 'selectedHosts' },
    { name: 'Support', key: 'selectedSupports' },
    { name: 'Registrants', key: 'selectedRegistrants' },
];

const SelectInput = (props: ISelectorProps) => {
    const {
        value,
        options,
        onChange
    } = props;
    const intl = useIntl();

    return (
        <select
            value={value}
            onChange={onChange}
            className='oe-form-field__control'
        >
            {
                options.map((opt: IOptions, i) => {
                    const {
                        label,
                        optValue
                    } = opt;
                    return (
                        <option key={i} value={optValue}>
                            {intl.formatMessage({ ...label })}
                        </option>
                    );
                })
            }
        </select>
    );
};

const MeetingFormPresentation = mobxReact.observer(({ vm }: IMeetingFormPresentationProps) => {
    const formData = vm.meetingFormDataVM;
    const defaultMeetingDuration = vm.event.defaultMeetingDuration;
    const eventTimezone = vm.event.timeZone;
    const eventDates = vm.getEventDatesArray();
    const intl = useIntl();

    useEffect(() => {
        return () => {
            formData?.disposeReactions();
        };
    }, [formData]);

    const replaysAvailableInfoMessage = intl.formatMessage(MEETING_TEXTS.REPLAYS_AVAILABLE_INFO_MESSAGE_TEXT);
    const featuredMeetingInfoMessage = intl.formatMessage(MEETING_TEXTS.FEATURED_MEETING_INFO_MESSAGE_TEXT);
    const audienceCapacityInfoMessage = intl.formatMessage(MEETING_TEXTS.AUDIENCE_CAPACITY_INFO_MESSAGE_TEXT);
    const inviteAdditionalAttendeesInfoMessage = intl.formatMessage(MEETING_TEXTS.INVITE_ADDITIONAL_ATTENDEES_INFO_MESSAGE_TEXT);

    if (!vm.isFormDataReady) {
        return <CommonPopupBodyFooterLayout body={
            <div className='oe-form meeting-form'>
                {intl.formatMessage(MEETING_TEXTS.MEETING_LOADING_DATA_TEXT, {
                    suffix: '...'
                })}
            </div>}
        />;
    }

    const isEdit = !!formData.getMeetingId();
    const dateTimeList = formData.getDateTimeList();
    const dateTimeListLength = dateTimeList.length;
    const addTitle = intl.formatMessage(COMMON_TEXTS.ADD_TEXT);
    const meetingTitle = intl.formatMessage(COMMON_TEXTS.MEETINGS_TEXT);
    const submitTitleForAddMeetings = dateTimeListLength > 1
        ? `${addTitle} ${dateTimeListLength} ${meetingTitle}`
        : addTitle;
    const submitTitle = isEdit ? intl.formatMessage(COMMON_TEXTS.APPLY_TEXT) : submitTitleForAddMeetings;

    const meetingOptions = [
        isEdit && hasPermission(Permission.RUN_MEETING) && {
            label: intl.formatMessage(MEETING_TEXTS.RUN_MEETING_TEXT),
            action: vm.runMeeting,
        },
        isEdit && hasPermission(Permission.CONNECTIONS_MANAGEMENT) && {
            label: intl.formatMessage(MEETING_TEXTS.EDIT_MEETING_CONNECTIONS_TEXT),
            action: vm.editMeetingConnections,
            disabled: !vm.isUniqueJoiningLinksDeletionTriggerEnabled,
        },
        isEdit && hasPermission(Permission.DELETE_MEETING) && {
            label: intl.formatMessage(MEETING_TEXTS.MEETING_DELETE_TEXT),
            action: vm.deleteMeeting,
            danger: true
        }
    ].filter(Boolean); // filter returns array without null or false elements

    const timeValidationError = formData.getTimeValidationErrorEdit();

    let disabledSubmitButton = null;
    if (isEdit) {
        disabledSubmitButton = !!(timeValidationError
            || formData.getMeetingTimesErrorEdit()
            || formData.getMeetingEndDateErrorEdit()
            || (formData.isFormChecked && formData.getMeetingEndDateRequiredErrorEdit())
            || formData.isImageUploading()
            || formData.expiryVM.hasValidationError()
            || formData.goLiveVM.hasValidationError()
            || formData.getAudienceCapacityValidationError()
        );
    }

    const isExternalIdDisabled = dateTimeList.length > 1;
    if (isExternalIdDisabled) {
        formData.setExternalId('');
        formData.setExternalIdValidationError(false);
    }

    const dateTimeFormatter = IoC.Container.get(IDateTimeFormatter);

    const dateFormat = dateTimeFormatter.getLocalTimeFormat() === TimeFormats.Day24Hours
        ? DateFnsFormats.StringifiedShortFullDateDay24Hours
        : DateFnsFormats.StringifiedShortFullDateDay12Hours;

    const shouldShowConnectionVersion = hasPermission(Permission.CREATE_MEETINGS_V2) && !isEdit;

    const getMeetingDateTimeView = () => {
        if (isEdit) {
            return (
                <>
                    <div className='meeting-form__label'>
                        <FormField className={classNames('meeting-form__form-field', { 'meeting-form__form-column': formData.showEndDate })}>
                            <div className='meeting-form__label'>
                                <label className='oe-form-field__title'>{intl.formatMessage(COMMON_TEXTS.DATE_TEXT)}</label>
                                {!formData.showEndDate &&
                                    <DefaultButton
                                        className='meeting-form__add-button'
                                        modifier='link'
                                        text={intl.formatMessage(MEETING_TEXTS.MEETING_CHANGE_END_DATE_TEXT)}
                                        onClick={() => formData.setShowEndDate(true)}
                                    />
                                }
                            </div>

                            <DefaultDatePicker
                                className='oe-form-field__control'
                                id='meetingDate'
                                dateformat={dateFormat}
                                showTimeSelect={false}
                                selected={formData.meetingDate}
                                timeZone={eventTimezone}
                                onChange={date => {
                                    const startDateTimeZoned = utcToZonedTime(date, eventTimezone);
                                    let endDateTimeZoned = null;
                                    let sameStartEndDate = false;
                                    if (formData.meetingEndDate) {
                                        endDateTimeZoned = utcToZonedTime(formData.meetingEndDate, eventTimezone);
                                        sameStartEndDate = isSameDay(startDateTimeZoned, endDateTimeZoned);
                                    }
                                    formData.setMeetingDate(date, eventTimezone, 'startDate', sameStartEndDate);
                                }}
                                highlightDates={eventDates}
                            />
                        </FormField>

                        {formData.showEndDate &&
                            <FormField
                                label={intl.formatMessage(COMMON_TEXTS.END_DATE_TEXT)}
                                className='meeting-form__form-field'
                            >
                                <DefaultDatePicker
                                    className='oe-form-field__control'
                                    id='meetingEndDate'
                                    dateformat={dateFormat}
                                    showTimeSelect={false}
                                    selected={formData.meetingEndDate}
                                    timeZone={eventTimezone}
                                    popperPlacement='bottom-end'
                                    onChange={date => {
                                        const startDateTimeZoned = utcToZonedTime(formData.meetingDate, eventTimezone);
                                        const endDateTimeZoned = utcToZonedTime(date, eventTimezone);
                                        formData.setMeetingDate(date, eventTimezone, 'endDate', isSameDay(startDateTimeZoned, endDateTimeZoned));
                                    }}
                                    highlightDates={eventDates}
                                    openToDate={formData.meetingEndDate || formData.meetingDate}
                                />
                            </FormField>}

                    </div>
                    <div className='meeting-form__error-block oe-form-field__error'>
                        {formData.getMeetingDateErrorEdit()}
                        {formData.isFormChecked && formData.getMeetingEndDateRequiredErrorEdit()}
                        {formData.getMeetingEndDateErrorEdit()}
                    </div>
                    <div className='meeting-form__label'>
                        <FormField
                            label={intl.formatMessage(COMMON_TEXTS.START_TIME_TEXT)}
                            className='meeting-form__form-field meeting-form__form-column'
                        >
                            <TimeInput
                                className='oe-form-field__control'
                                date={formData.startDate}
                                disabled={!formData.meetingDate}
                                onChange={date => formData.setStartTime(date, eventTimezone)}
                                timeZone={eventTimezone}
                                onError={() => formData.setStartTimeValidationErrorEdit(true)}
                            />
                        </FormField>
                        <FormField
                            label={intl.formatMessage(COMMON_TEXTS.END_TIME_TEXT)}
                            className='meeting-form__form-field'
                        >
                            <TimeInput
                                className='oe-form-field__control'
                                date={formData.endDate}
                                disabled={!formData.meetingDate}
                                onChange={date => formData.setEndTime(date, eventTimezone)}
                                timeZone={eventTimezone}
                                onError={() => formData.setEndTimeValidationErrorEdit(true)}
                            />
                        </FormField>
                    </div>
                    <span className='oe-form-field__error meeting-form__error-block'>
                        {timeValidationError ? timeValidationError : formData.getMeetingTimesErrorEdit()}
                    </span>
                </>
            );
        }

        return (
            <>
                {dateTimeList.map(({ meetingDate, startDate, endDate }, index) => {
                    return (
                        <DateTimeFormView
                            key={index}
                            formData={formData}
                            index={index}
                            eventTimezone={eventTimezone}
                            meetingDate={meetingDate}
                            startDate={startDate}
                            endDate={endDate}
                            dateTimeListLength={dateTimeListLength}
                            highlightDates={eventDates}
                            defaultMeetingDuration={defaultMeetingDuration}
                            dateformat={dateFormat}
                        />
                    );
                })}
                <DefaultButton
                    className='add-line-to-table-btn m-top-15px m-bottom-25px'
                    // check
                    text={intl.formatMessage(MEETING_TEXTS.ADD_MEETING_TIME_TEXT, { prefix: '+ ' })}
                    disabled={!dateTimeList[dateTimeListLength - 1].meetingDate || (dateTimeListLength >= MAX_MEETING_DATES_LIST_LENGTH)}
                    onClick={() => {
                        formData.addNewValueInDateTimeList(dateTimeListLength);
                    }}
                />
            </>
        );
    };

    return (
        <CommonPopupBodyFooterLayout
            body={(
                <form className='oe-form meeting-form' onSubmit={vm.formSubmitAction}>
                    <div className='oe-form__body'>
                        {shouldShowConnectionVersion && <FormField className='m-bottom-20px'>
                            <div className='oe-form__row version-toggle'>
                                <OEToggle
                                    isSelected={formData.getConnectionVersion()}
                                    onClick={() => formData.setConnectionVersion(!formData.getConnectionVersion())}
                                    isDisabled={!isSuperUser()}
                                />
                                <Text
                                    {...V2_CONNECTIONS_TEXT}
                                    values={{
                                        suffix: formData.getConnectionVersion() ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                    }}
                                />
                            </div>
                        </FormField>}
                        {
                            formData.shouldShowTemplateLabel() &&
                            <div className='meeting-template-name'>
                                <span className='meeting-template-name--label'>
                                    {/* check */}
                                    {intl.formatMessage(MEETING_TEXTS.MEETING_TEMPLATE_SUFFIX_TEXT, { suffix: ':' })}
                                </span>
                                <Text className='meeting-template-name--value' message={formData.getTemplateName()} withEllipsis />
                            </div>
                        }
                        {getMeetingDateTimeView()}
                        {
                            formData.canApplyTemplateToMeeting() &&
                            <FormField
                                // chekc
                                label={intl.formatMessage(MEETING_TEXTS.MEETING_TEMPLATE_TEXT)}
                            >
                                <select
                                    className='oe-form-field__control'
                                    value={formData.getSelectedTemplateId()}
                                    onChange={e => formData.selectTemplate(e.target.value)}
                                    disabled={formData.isLoadingTemplates}
                                >
                                    <option value={''}>{intl.formatMessage(MEETING_TEXTS.NO_TEMPLATE_SELECTED_TEXT)}</option>
                                    {
                                        formData.getTemplatesOptions().map(({ blueprintId, name }) => {
                                            return <option value={blueprintId} key={blueprintId}>{name}</option>;
                                        })
                                    }
                                </select>
                                <ViewBlueprintDetailsSection
                                    blueprintDetails={formData.getSelectedMeetingTemplateDetails()}
                                    blueprintItemType={formData.getSelectedMeetingType()}
                                />
                            </FormField>
                        }
                        <div className='flex-wrap'>
                            <FormField className='meeting-form__form-column' label={intl.formatMessage(MEETING_TEXTS.MEETING_STATUS_TEXT)}>
                                <select
                                    value={formData.status}
                                    onChange={mobx.action(e => formData.status = e.target.value as MeetingStatus)}
                                    className='oe-form-field__control'
                                >
                                    <option value={MeetingStatus.Scheduled}>{intl.formatMessage(MEETING_TEXTS.MEETING_SCHEDULED_TEXT)}</option>
                                    <option value={MeetingStatus.Cancelled}>{intl.formatMessage(MEETING_TEXTS.MEETING_CANCELLED_TEXT)}</option>
                                    <option value={MeetingStatus.Removed}>{intl.formatMessage(MEETING_TEXTS.MEETING_REMOVED_TEXT)}</option>
                                </select>
                            </FormField>
                            <FormField style={{ width: '30%' }} label={intl.formatMessage(MEETING_TEXTS.MEETING_ACCESS_TEXT)}>
                                <select
                                    value={formData.access}
                                    onChange={formData.handleChangeMeetingAccess}
                                    className='oe-form-field__control'
                                >
                                    <option value={Access.Closed}>{intl.formatMessage(MEETING_TEXTS.MEETING_CLOSED_TEXT)}</option>
                                    <option value={Access.Open}>{intl.formatMessage(MEETING_TEXTS.MEETING_OPEN_TEXT)}</option>
                                    <option value={Access.Plenary}>{intl.formatMessage(MEETING_TEXTS.MEETING_PLENARY_TEXT)}</option>
                                    <option value={Access.Bookable}>{intl.formatMessage(MEETING_TEXTS.MEETING_BOOKABLE_TEXT)}</option>
                                </select>
                            </FormField>
                        </div>
                        <div className='recording__label-wrapper'>
                            <OEToggle
                                isSelected={formData.meetingRecordEnabled}
                                onClick={formData.toggleMeetingRecordeEnabled}
                            />
                            <Text message={`Record Meeting: ${formData.meetingRecordEnabled ? RecordingsStatus.Yes : RecordingsStatus.No}`} />
                        </div>
                        <div className='m-bottom-25px flex meeting-toggle'>
                            <div>
                                <OEToggle
                                    isSelected={formData.replayEnabled}
                                    onClick={formData.toggleReplayEnabledMeeting}
                                    isDisabled={formData.access === Access.Closed || formData.access === Access.Bookable}
                                />
                                {/* //checkk */}
                                <Text
                                    {...MEETING_TEXTS.ENABLE_ON_REPLAYS_PAGE_TEXT}
                                    values={{
                                        prefix: formData.replayEnabled ? 'Meeting is' : 'Not'
                                    }}
                                    className='replays-toggle-description'
                                />
                            </div>
                            <InfoPopover>
                                <div>{replaysAvailableInfoMessage}</div>
                            </InfoPopover>
                        </div>
                        <div className='m-bottom-25px flex meeting-toggle'>
                            <div className='featured-toggle'>
                                <OEToggle isSelected={formData.featured} onClick={formData.toggleFeaturedMeeting} />
                                <Text {...MEETING_TEXTS.FEATURED_PRESENTATION_TEXT} />
                            </div>
                            <InfoPopover>
                                <div>{featuredMeetingInfoMessage}</div>
                            </InfoPopover>
                        </div>
                        {isEdit && (
                            <MeetingGoLiveDate
                                vm={formData.goLiveVM}
                                eventTimezone={eventTimezone}
                                dateFormat={dateFormat}
                            />
                        )}
                        {isEdit && <MeetingExpiryDate vm={formData.expiryVM} />}
                        {formData.shouldShowAudienceCapacity() && (
                            <>
                                <div className='m-bottom-15px flex meeting-toggle'>
                                    <div>
                                        <OEToggle
                                            isSelected={formData.isAudienceCapacityEnabled}
                                            onClick={formData.toggleAudienceCapacityEnabled}
                                        />
                                        <Text
                                            {...MEETING_TEXTS.AUDIENCE_CAPACITY_STATUS_TEXT}
                                            values={{
                                                suffix: formData.isAudienceCapacityEnabled ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                            }}
                                        />
                                    </div>
                                    <InfoPopover>
                                        <div>{audienceCapacityInfoMessage}</div>
                                    </InfoPopover>
                                </div>
                                {formData.isAudienceCapacityEnabled && (
                                    <FormField label={intl.formatMessage(MEETING_TEXTS.AUDIENCE_CAPACITY_TEXT)}>
                                        <div className='flex'>
                                            <input
                                                className='oe-form-field__control'
                                                type='number'
                                                min={1}
                                                max={99999}
                                                step={1}
                                                value={formData.audienceCapacity}
                                                onChange={mobx.action(e => formData.setAudienceCapacity(e.target.value))}
                                            />
                                            <span className='m-left-10px m-right-10px'>
                                                {intl.formatMessage(MEETING_TEXTS.AUDIENCE_ATTENDEES_TEXT)}
                                            </span>
                                            <InfoPopover>
                                                <em>{intl.formatMessage(MEETING_TEXTS.AUDIENCE_CAPACITY_RANGE_TEXT)}</em>
                                            </InfoPopover>
                                        </div>
                                        {formData.getAudienceCapacityValidationError() && (
                                            <span className='oe-form-field__error'>{formData.getAudienceCapacityValidationError()}</span>
                                        )}
                                    </FormField>
                                )}
                            </>
                        )}
                        {formData.shouldShowInviteAdditionalAttendees() && (
                            <div className='m-bottom-15px flex meeting-toggle'>
                                <div>
                                    <OEToggle
                                        isSelected={formData.isInviteAdditionalAttendeesEnabled}
                                        onClick={formData.toggleInviteAdditionalAttendeesEnabled}
                                    />
                                    <Text
                                        {...MEETING_TEXTS.INVITE_ADDITIONAL_ATTENDEES_STATUS_TEXT}
                                        values={{
                                            suffix: formData.isInviteAdditionalAttendeesEnabled ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                        }}
                                    />
                                </div>
                                <InfoPopover>
                                    <div>{inviteAdditionalAttendeesInfoMessage}</div>
                                </InfoPopover>
                            </div>
                        )}
                        <FormField label={intl.formatMessage(COMMON_TEXTS.ROOM_TEXT)}>
                            <RoomsDropdown
                                selectedRoomId={formData.room.roomId}
                                timeZone={vm.event.timeZone}
                                rooms={vm.narrowedRooms}
                                onChange={mobx.action(roomId => formData.room.roomId = roomId)}
                                meetingConnectionVersion={formData.getParsedConnectionVersion()}
                            />
                        </FormField>
                        {roles.map(({ name, key }) => {
                            return <AttendeesList
                                key={name}
                                name={name}
                                attendees={vm.attendees}
                                selected={vm[key]}
                                onClick={vm.updateOrganizationsList}
                                onChange={vm.addAttendeesByKey.bind(this, key)}
                                removeOrganization={mobx.action(id => vm[key] = vm.organizationFilter(vm[key], id))}
                                removeAttendee={mobx.action(id => vm[key] = vm.attendeesFilter(vm[key], id))}
                                disabled={getDisabled([
                                    ...vm.selectedHosts,
                                    ...vm.selectedPresenters,
                                    ...vm.selectedParticipants,
                                    ...vm.selectedRegistrants,
                                    ...vm.selectedSupports
                                ])}
                                ActionButton={() => <AddNewEntityButton
                                    permission={Permission.CREATE_ATTENDEE}
                                    onClick={() =>
                                        vm.createAttendee((attendee: IAttendeeDetailsDTO) => vm.addNewAttendeeByRole(attendee, key))}
                                    // check
                                    label={intl.formatMessage(MEETING_TEXTS.ATTENDEE_NEW_TEXT, { prefix: '+ ' })}
                                />
                                }
                            />;
                        })}
                        <FormField label={intl.formatMessage(COMMON_TEXTS.TYPE)}>
                            <input
                                className='oe-form-field__control'
                                id='meeting-type'
                                value={formData.type}
                                onChange={mobx.action(e => formData.type = e.target.value)}
                            />
                            {formData.getFieldLengthError('type') && <span className='oe-form-field__error'>{formData.getFieldLengthError('type')}</span>}
                        </FormField>
                        <FormField label={intl.formatMessage(MEETING_TEXTS.MEETING_TRACK_TEXT)}>
                            <input
                                className='oe-form-field__control'
                                id='meeting-track'
                                value={formData.track}
                                onChange={mobx.action(e => formData.track = e.target.value)}
                            />
                            {formData.getFieldLengthError('track') && <span className='oe-form-field__error'>{formData.getFieldLengthError('track')}</span>}
                        </FormField>
                        <FormField label={intl.formatMessage(COMMON_TEXTS.TOPIC_TEXT)}>
                            <input
                                className='oe-form-field__control'
                                type='text'
                                id='topic'
                                value={formData.topic}
                                onChange={mobx.action(e => formData.topic = e.target.value)}
                            />
                            {formData.getFieldLengthError('topic') && <span className='oe-form-field__error'>{formData.getFieldLengthError('topic')}</span>}
                        </FormField>
                        {isEdit &&
                            <UploadImageS3Presentation
                                vm={formData.uploadThumbnailImageS3VM}
                                dimensions={1000}
                                fieldName={intl.formatMessage(MEETING_TEXTS.SESSION_THUMBNAIL_IMAGE_TEXT)}
                            />
                        }

                        {isEdit &&
                            <UploadImageS3Presentation
                                vm={formData.uploadHeroImageS3VM}
                                dimensions={3000}
                                fieldName={intl.formatMessage(MEETING_TEXTS.SESSION_HERO_IMAGE_TEXT)}
                            />
                        }

                        <Links
                            links={formData.links}
                            eventId={vm.event.eventId}
                            accountId={vm.event.accountId}
                            meetingId={vm.meetingId}
                            setAddedLinks={formData.setAddedLinks}
                            removeAddedLinks={formData.removeAddedLinks}
                            getAddedLinks={formData.getAddedLinks}
                        />

                        <FormField label={intl.formatMessage(MEETING_TEXTS.NOTES_FOR_PASSPORT_TEXT)}>
                            <RichTextEditor
                                id='meeting-notes'
                                value={formData.notes}
                                onChange={mobx.action((event, editor) => {
                                    formData.notes = editor.getData();
                                })}
                                validationError={formData.getRichTextFieldLengthError('notes')}
                            />
                        </FormField>
                        <FormField label={intl.formatMessage(COMMON_TEXTS.EXTERNAL_ID_TEXT)}>
                            <>
                                <AsyncSearchInput
                                    disabled={isExternalIdDisabled}
                                    className='oe-form-field__control'
                                    onChange={mobx.action(value => {
                                        vm.refreshExternalIdError();
                                        formData.setExternalId(value);
                                    })}
                                    value={formData.getExternalId()}
                                />
                                {formData.getFieldLengthError('externalId') && <span className='oe-form-field__error'>{formData.getFieldLengthError('externalId')}</span>}
                                {vm.getExistingExternalIdError() &&
                                <span className='oe-form-field__error'>{vm.getExistingExternalIdError()}</span>}
                            </>
                        </FormField>
                        {isEdit && (
                            <>
                                <FormField label={intl.formatMessage(MEETING_TEXTS.MEETING_PATH_TEXT)}>{formData.path}</FormField>
                                <FormField label={intl.formatMessage(MEETING_TEXTS.MEETING_PASSCODE_TEXT)}>{formData.passcode}</FormField>
                            </>
                        )}
                        {formData.meetingLink &&
                            <FormField label={intl.formatMessage(MEETING_TEXTS.MEETING_LINK_TEXT)}>
                                <div className='flex'>
                                    <input
                                        className='oe-form-field__control'
                                        id='meeting-link'
                                        disabled
                                        value={formData.meetingLink}
                                    />
                                    <div className='flex' onClick={mobx.action(() => {
                                        formData.meetingLinkClick = true;
                                    })}>
                                        <CopyButton copyValue={formData.meetingLink} className='m-left-6px' />
                                    </div>
                                </div>
                            </FormField>
                        }
                        {formData.meetingLinkWithEmbeddedBypassCode &&
                            <FormField label={intl.formatMessage(MEETING_TEXTS.LINK_WITH_EMBEDDED_PASSCODE_TEXT)}>
                                <div className='flex'>
                                    <input
                                        className='oe-form-field__control'
                                        id='meeting-link-with-passcode'
                                        disabled={true}
                                        value={formData.meetingLinkWithEmbeddedBypassCode}
                                    />
                                    <div className='flex' onClick={mobx.action(() => {
                                        formData.meetingLinkWithEmbeddedBypassCodeClick = true;
                                    })}>
                                        <CopyButton
                                            copyValue={formData.meetingLinkWithEmbeddedBypassCode}
                                            className='m-left-6px'
                                        />
                                    </div>
                                </div>
                            </FormField>
                        }
                            {isEdit && (
                                <FormField className='w-50' label={intl.formatMessage(MEETING_TEXTS.MEETING_ID_TEXT)}>
                                    {vm.meetingId}
                                </FormField>
                            )}
                    </div>
                </form>
            )}
            footer={
                <>
                    {vm.saving && intl.formatMessage(MEETING_TEXTS.MEETING_IS_BEING_SAVED_TEXT)}
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={submitTitle}
                            modifier={['fat', 'confirm-safe']}
                            onClick={vm.formSubmitAction}
                            disabled={disabledSubmitButton || vm.saving}
                        />
                        <DefaultCloseSubWindowButton text={intl.formatMessage(COMMON_TEXTS.CANCEL_TEXT)} />
                        {!!meetingOptions.length && <OptionsButton
                            className='options-button--right-margin-auto'
                            modifier={['fat', 'options']}
                            label={intl.formatMessage(COMMON_TEXTS.MORE_BUTTON_TEXT)}
                            options={meetingOptions}
                            position='top'
                            align='start'
                        />}
                    </div>
                </>
            }
        />
    );
});

export { SelectInput, MeetingFormPresentation };
